import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const Blog = () => {
  const { t } = useTranslation();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [visibleSections, setVisibleSections] = useState({});

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setVisibleSections((prev) => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting,
          }));
        });
      },
      { threshold: 0.15, rootMargin: '-80px' }
    );

    const sections = document.querySelectorAll('.animate-section');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [loading]);

  // Fetch blog posts from API
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get('/api/blog');
        if (response.data.success) {
          setBlogs(response.data.data);
          console.log('Blog source:', response.data.source);
        } else {
          setBlogs(staticBlogs);
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setBlogs(staticBlogs);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const staticBlogs = [
    {
      id: 1,
      slug: 'ductless-vs-ducted-laminar-air-flow',
      title: 'Ductless vs Ducted Laminar Air Flow: Which is Better for Your Hospital?',
      excerpt: 'Understanding the key differences between ductless and ducted laminar air flow systems and why ALMED\'s patented ductless system offers superior infection control.',
      category: 'Products',
      author: 'ALMED Team',
      date: '2025-01-15',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800',
      readTime: '5 min read',
      featured: true
    },
    {
      id: 2,
      slug: 'modular-ot-complete-guide',
      title: 'Complete Guide to Modular Operation Theatres',
      excerpt: 'Everything you need to know about setting up a modular operation theatre - from design to installation and maintenance.',
      category: 'Products',
      author: 'ALMED Team',
      date: '2025-01-10',
      image: 'https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=800',
      readTime: '8 min read',
      featured: true
    },
    {
      id: 3,
      slug: 'psa-oxygen-plant-benefits',
      title: 'Why Every Hospital Needs a PSA Oxygen Generation Plant',
      excerpt: 'Discover how on-site oxygen generation can save costs, ensure supply reliability, and improve patient care.',
      category: 'Products',
      author: 'ALMED Team',
      date: '2025-01-05',
      image: 'https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=800',
      readTime: '6 min read',
      featured: false
    },
    {
      id: 4,
      slug: 'infection-control-healthcare',
      title: 'Infection Control in Healthcare: Best Practices for 2025',
      excerpt: 'Learn about the latest infection control measures and how proper air quality management can prevent hospital-acquired infections.',
      category: 'Healthcare',
      author: 'ALMED Team',
      date: '2024-12-28',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800',
      readTime: '7 min read',
      featured: false
    },
    {
      id: 5,
      slug: 'almed-mt-healthcare-award-2018',
      title: 'ALMED Wins MT India Healthcare Award 2018',
      excerpt: 'We are proud to announce that ALMED Equipment has been recognized for innovative solutions in infection control at the 8th MT India Healthcare Awards.',
      category: 'Achievements',
      author: 'ALMED Team',
      date: '2024-12-20',
      image: 'https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?w=800',
      readTime: '3 min read',
      featured: false
    },
    {
      id: 6,
      slug: 'ozone-generator-disinfection',
      title: 'Ozone Generators: Chemical-Free Disinfection for Modern Hospitals',
      excerpt: 'How ozone technology is revolutionizing hospital disinfection without the use of harmful chemicals.',
      category: 'Products',
      author: 'ALMED Team',
      date: '2024-12-15',
      image: 'https://images.unsplash.com/photo-1581093458791-9d42cc030552?w=800',
      readTime: '5 min read',
      featured: false
    },
    {
      id: 7,
      slug: 'iso-certification-importance',
      title: 'Why ISO Certification Matters in Healthcare Equipment',
      excerpt: 'Understanding the importance of ISO 9001:2008 certification and what it means for product quality and reliability.',
      category: 'Company',
      author: 'ALMED Team',
      date: '2024-12-10',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800',
      readTime: '4 min read',
      featured: false
    },
    {
      id: 8,
      slug: 'government-hospital-projects',
      title: 'ALMED\'s Government Hospital Projects: A Success Story',
      excerpt: 'How we have successfully delivered healthcare equipment solutions to government hospitals across India.',
      category: 'Achievements',
      author: 'ALMED Team',
      date: '2024-12-05',
      image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800',
      readTime: '6 min read',
      featured: false
    }
  ];

  const categories = [
    { id: 'all', labelKey: 'blog.categories.all' },
    { id: 'Products', labelKey: 'blog.categories.products' },
    { id: 'Healthcare', labelKey: 'blog.categories.healthcare' },
    { id: 'Achievements', labelKey: 'blog.categories.achievements' },
    { id: 'Company', labelKey: 'blog.categories.company' }
  ];

  const filteredBlogs = activeCategory === 'all' 
    ? blogs 
    : blogs.filter(blog => blog.category === activeCategory);

  const featuredBlogs = blogs.filter(blog => blog.featured);
  const regularBlogs = filteredBlogs.filter(blog => !blog.featured || activeCategory !== 'all');

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <div className="loading" style={{minHeight: '100vh', marginTop: '70px'}}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="blog-page" style={{marginTop: '70px'}}>
      {/* Hero Section */}
      <section className="hero" style={{minHeight: '40vh'}}>
        <div className="hero-content">
          <p className="hero-subtitle">{t('blog.badge')}</p>
          <h1 className="hero-title">{t('blog.title')}</h1>
          <p className="hero-description">
            {t('blog.description')}
          </p>
        </div>
      </section>

      {/* Featured Posts */}
      {activeCategory === 'all' && featuredBlogs.length > 0 && (
        <section id="featured" className="section animate-section">
          <div className="container">
            <div 
              className="section-header"
              style={{
                opacity: visibleSections['featured'] ? 1 : 0,
                transform: visibleSections['featured'] ? 'translateY(0)' : 'translateY(40px)',
                transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <p className="section-subtitle">{t('blog.featured')}</p>
              <h2 className="section-title">{t('blog.topArticles')}</h2>
            </div>

            <div className="blog-featured-grid">
              {featuredBlogs.map((blog, index) => (
                <Link 
                  key={blog.id} 
                  to={`/blog/${blog.slug}`} 
                  className={`blog-featured-card ${index === 0 ? 'large' : ''}`}
                  style={{
                    backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.8) 100%), url(${blog.image})`
                  }}
                >
                  <div className="blog-featured-content">
                    <span className="blog-category">{blog.category}</span>
                    <h3 className="blog-featured-title">{blog.title}</h3>
                    <div className="blog-meta">
                      <span className="blog-author">{t('blog.by')} {blog.author}</span>
                      <span className="blog-read-time">{blog.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section id="blog-content" className="section animate-section">
        <div className="container">
          <div 
            className="blog-categories"
            style={{
              opacity: visibleSections['blog-content'] ? 1 : 0,
              transform: visibleSections['blog-content'] ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`blog-category-btn ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {t(cat.labelKey)}
              </button>
            ))}
          </div>

          {/* Blog Grid - Pinterest Style */}
          <div className="blog-masonry-grid">
            {regularBlogs.map((blog, index) => (
              <Link 
                key={blog.id} 
                to={`/blog/${blog.slug}`} 
                className="blog-card"
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div className="blog-card-image">
                  <img src={blog.image} alt={blog.title} />
                  <span className="blog-card-category">{blog.category}</span>
                </div>
                <div className="blog-card-content">
                  <h3 className="blog-card-title">{blog.title}</h3>
                  <p className="blog-card-excerpt">{blog.excerpt}</p>
                  <div className="blog-card-footer">
                    <div className="blog-card-author">
                      <div className="author-avatar">
                        {blog.author.charAt(0)}
                      </div>
                      <span>{t('blog.by')} {blog.author}</span>
                    </div>
                    <span className="blog-card-date">{formatDate(blog.date)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredBlogs.length === 0 && (
            <div className="no-results">
              <p>{t('blog.noResults')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="cta-section">
        <div className="container">
          <h2 className="cta-title">{t('blog.stayUpdated.title')}</h2>
          <p className="cta-description">
            {t('blog.stayUpdated.description')}
          </p>
          <Link to="/contact" className="btn btn-white">{t('blog.stayUpdated.contactUs')}</Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;

